import { lazy, Suspense, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, RotateCcw } from 'lucide-react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { DesktopProvider, useDesktop, WindowDataProvider, type AppId } from './DesktopContext';
import { AppWindow } from './AppWindow';
import { Dock } from './Dock';
import { CommandPalette } from './CommandPalette';
import { LoginArea } from '@/components/auth/LoginArea';
import { DesktopShortcut } from './components/DesktopShortcut';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Button } from '@/components/ui/button';
import { useAppContext } from '@/hooks/useAppContext';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

// Each app loads as its own chunk the first time a window opens, keeping the
// initial desktop bundle small.
const APP_COMPONENTS: Record<AppId, React.ComponentType> = {
  feed: lazy(() => import('./apps/FeedApp').then((m) => ({ default: m.FeedApp }))),
  dms: lazy(() => import('./apps/DMsApp').then((m) => ({ default: m.DMsApp }))),
  relays: lazy(() => import('./apps/RelaysApp').then((m) => ({ default: m.RelaysApp }))),
  media: lazy(() => import('./apps/MediaApp').then((m) => ({ default: m.MediaApp }))),
  nsite: lazy(() => import('./apps/NsiteBrowserApp').then((m) => ({ default: m.NsiteBrowserApp }))),
  profile: lazy(() => import('./apps/ProfileApp').then((m) => ({ default: m.ProfileApp }))),
  settings: lazy(() => import('./apps/SettingsApp').then((m) => ({ default: m.SettingsApp }))),
  notifications: lazy(() => import('./apps/NotificationsApp').then((m) => ({ default: m.NotificationsApp }))),
  articles: lazy(() => import('./apps/ArticlesApp').then((m) => ({ default: m.ArticlesApp }))),
  publish: lazy(() => import('./apps/PublishApp').then((m) => ({ default: m.PublishApp }))),
  mail: lazy(() => import('./apps/MailApp').then((m) => ({ default: m.MailApp }))),
  documents: lazy(() => import('./apps/DocumentsApp').then((m) => ({ default: m.DocumentsApp }))),
  search: lazy(() => import('./apps/SearchApp').then((m) => ({ default: m.SearchApp }))),
};

function AppLoading() {
  return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
}

/** Escape a sanitized URL for safe interpolation into a CSS `url("...")`. */
function cssUrl(url: string): string {
  return `url("${url.replace(/["\\]/g, '\\$&')}")`;
}

function DesktopWallpaper() {
  const { wallpaper } = useDesktop();
  const { metadata } = useCurrentUser();

  // Event-sourced URLs (profile banner) must be sanitized before they are
  // interpolated into CSS.
  const bgImage = sanitizeUrl(wallpaper) ?? sanitizeUrl(metadata?.banner) ?? null;

  return (
    <div
      className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: bgImage ? cssUrl(bgImage) : undefined,
        backgroundColor: bgImage ? undefined : 'hsl(var(--background))',
      }}
    >
      {!bgImage && (
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
      )}
      <div className="absolute inset-0 bg-black/20" />
    </div>
  );
}

function WindowRenderer() {
  const { windows } = useDesktop();

  return (
    <>
      {windows.map((win) => {
        const AppComponent = APP_COMPONENTS[win.appId];
        return (
          <AppWindow key={win.id} window={win}>
            <WindowDataProvider data={win.data}>
              {/* One app crashing must not take the whole desktop down — the
                  error is contained inside the window with a retry affordance. */}
              <ErrorBoundary
                fallbackRender={({ reset }) => (
                  <div className="flex flex-col items-center justify-center h-full gap-3 p-6 text-center">
                    <AlertTriangle className="w-8 h-8 text-destructive/70" />
                    <div>
                      <p className="text-sm font-medium">This app ran into a problem</p>
                      <p className="text-xs text-muted-foreground mt-1">Your other windows are unaffected.</p>
                    </div>
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={reset}>
                      <RotateCcw className="w-3.5 h-3.5" />
                      Reload app
                    </Button>
                  </div>
                )}
              >
                <Suspense fallback={<AppLoading />}>
                  <AppComponent />
                </Suspense>
              </ErrorBoundary>
            </WindowDataProvider>
          </AppWindow>
        );
      })}
    </>
  );
}

function LoginScreen() {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
      <div
        className="absolute inset-0 opacity-[0.15] dark:opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(hsl(var(--foreground) / 0.35) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground) / 0.35) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 45%, black, transparent)',
          WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 45%, black, transparent)',
        }}
      />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] rounded-full bg-primary/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center gap-7 p-8 animate-in fade-in-0 zoom-in-95 duration-300">
        <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-2xl shadow-primary/20 ring-1 ring-white/20">
          <span className="text-3xl font-bold text-primary-foreground">N</span>
        </div>
        <div className="text-center space-y-1.5">
          <h1 className="text-3xl font-bold tracking-tight">nostrVM</h1>
          <p className="text-sm text-muted-foreground">A Nostr desktop environment in your browser</p>
        </div>
        <LoginArea className="w-64" />
        <p className="text-[11px] text-muted-foreground/70">
          Tip: press <kbd className="px-1.5 py-0.5 rounded border border-border/60 bg-muted/60 font-mono text-[10px]">⌘K</kbd> anywhere on the desktop to launch apps
        </p>
      </div>
    </div>
  );
}

function DesktopShortcuts() {
  const { config } = useAppContext();
  const shortcuts = config.desktopShortcuts ?? [];

  // Track viewport height so the column layout reflows on window resize.
  const [viewportH, setViewportH] = useState(() => window.innerHeight);
  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Lay out shortcuts in a vertical column starting near top-left
  const ICON_WIDTH = 88;   // matches DesktopShortcut container width
  const ICON_HEIGHT = 104; // 64px icon + 8px gap + ~24px label
  const COL_GAP = 20;      // horizontal space between columns
  const ROW_GAP = 20;      // vertical space between rows
  const PADDING = 20;
  const rowsPerCol = Math.max(1, Math.floor((viewportH - 80) / (ICON_HEIGHT + ROW_GAP)));

  return (
    <>
      {shortcuts.map((shortcut, index) => {
        const col = Math.floor(index / rowsPerCol);
        const row = index % rowsPerCol;
        const x = PADDING + col * (ICON_WIDTH + COL_GAP);
        const y = PADDING + row * (ICON_HEIGHT + ROW_GAP);
        return (
          <DesktopShortcut
            key={shortcut.url}
            shortcut={shortcut}
            initialX={x}
            initialY={y}
          />
        );
      })}
    </>
  );
}

function DesktopContent() {
  const { user } = useCurrentUser();

  if (!user) {
    return <LoginScreen />;
  }

  return (
    <>
      <DesktopWallpaper />
      <DesktopShortcuts />
      <WindowRenderer />
      <Dock />
      <CommandPalette />
    </>
  );
}

export function DesktopEnvironment() {
  return (
    <DesktopProvider>
      <div className="fixed inset-0 overflow-hidden">
        <DesktopContent />
      </div>
    </DesktopProvider>
  );
}
